import type { BraceletStyle, MotifKind, Sizing } from '../engine/types';

/**
 * A structured design brief: the bridge between "something like a sunset" and
 * the deterministic pattern engine.
 *
 * This is the ONLY thing an AI is ever allowed to produce in Knotwright. It
 * carries intent — colours, mood, style leaning, a word to spell — and nothing
 * about knots. All geometry is generated downstream by code that can be tested.
 */
export interface DesignBrief {
  /** Whatever she typed, kept verbatim so she can see what was interpreted. */
  text: string;
  /** Chosen floss, as DMC codes. Order matters: first is the lead colour. */
  colors: string[];
  /** Style leaning. 'any' lets the generator offer a spread. */
  stylePreference: BraceletStyle | 'any';
  /** A short word to spell out in an alpha pattern, if she asked for one. */
  word?: string;
  /** Motifs she asked for by name (cherries, daisies...). */
  motifs: MotifKind[];
  /** Descriptive mood, used to name and blurb the results. */
  mood: string;
  sizing: Sizing;
  /** How this brief was produced, so the UI can be honest about it. */
  source: BriefSource;
  /** One-line, user-facing explanation of the above. */
  sourceNote: string;
}

export type BriefSource = 'heuristic' | 'gemini' | 'photo' | 'manual';

export const DEFAULT_SIZING: Sizing = {
  wristCm: 15.5,
  easeCm: 1.5,
  closure: 'tie',
};

/**
 * Standard wrist sizes.
 *
 * Population averages from anthropometric summaries (NHANES-derived crafter
 * references). Individual variation is large, so the UI always offers a
 * "measure it" field and treats these purely as starting points.
 */
export interface WristPreset {
  id: string;
  label: string;
  cm: number;
  inches: string;
}

export const WRIST_PRESETS: readonly WristPreset[] = [
  { id: 'child-small', label: 'Small child (4–6)', cm: 11.5, inches: '4½ in' },
  { id: 'child', label: 'Child (7–10)', cm: 12.5, inches: '5 in' },
  { id: 'teen', label: 'Preteen / teen', cm: 14, inches: '5½ in' },
  { id: 'adult-s', label: 'Adult — small', cm: 14.5, inches: '5¾ in' },
  { id: 'adult-m', label: 'Adult — medium', cm: 15.5, inches: '6 in' },
  { id: 'adult-l', label: 'Adult — large', cm: 17, inches: '6¾ in' },
  { id: 'adult-xl', label: 'Adult — extra large', cm: 18.5, inches: '7¼ in' },
] as const;

export const EASE_PRESETS = [
  { id: 'snug', label: 'Snug', cm: 0.5 },
  { id: 'standard', label: 'Comfortable', cm: 1.5 },
  { id: 'loose', label: 'Loose / stackable', cm: 2.5 },
] as const;
